// WebAuthn helpers for passkey registration and authentication
// Handles base64url encoding/decoding required by the WebAuthn API

import { fetchWithCsrf } from './csrf';

// base64url encode an ArrayBuffer
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// base64url decode to an ArrayBuffer
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Register a new passkey for the current user
export async function registerPasskey(label: string): Promise<void> {
  // 1. Get challenge from server
  const challengeRes = await fetchWithCsrf('/passkeys/challenge', {
    method: 'POST',
  });
  if (!challengeRes.ok) throw new Error('Failed to get registration challenge');
  const options = await challengeRes.json();

  // 2. Decode server options for the browser API
  options.user.id = base64urlToBuffer(options.user.id);
  options.challenge = base64urlToBuffer(options.challenge);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(
      (c: { id: string; type: string }) => ({
        ...c,
        id: base64urlToBuffer(c.id),
      }),
    );
  }

  // 3. Create credential via browser
  const credential = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential;
  if (!credential) throw new Error('Passkey creation cancelled');

  const response = credential.response as AuthenticatorAttestationResponse;

  // 4. Send credential to server
  const createRes = await fetchWithCsrf('/passkeys', {
    method: 'POST',
    body: JSON.stringify({
      label,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(response.attestationObject),
          clientDataJSON: bufferToBase64url(response.clientDataJSON),
        },
      },
    }),
  });

  if (!createRes.ok) {
    const err = await createRes.json();
    throw new Error(err.error || 'Failed to register passkey');
  }
}

// Server refused the assertion for a reason the user can act on (e.g. locked account)
export class PasskeyRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PasskeyRejectedError';
  }
}

// Authenticate with a passkey
export async function authenticateWithPasskey(): Promise<string> {
  // 1. Get challenge from server
  const challengeRes = await fetchWithCsrf('/passkey_session/challenge', {
    method: 'POST',
  });
  if (!challengeRes.ok) throw new Error('Failed to get authentication challenge');
  const options = await challengeRes.json();

  // 2. Decode server options for the browser API
  options.challenge = base64urlToBuffer(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map(
      (c: { id: string; type: string }) => ({
        ...c,
        id: base64urlToBuffer(c.id),
      }),
    );
  }

  // 3. Get credential via browser
  const credential = (await navigator.credentials.get({
    publicKey: options,
  })) as PublicKeyCredential;
  if (!credential) throw new Error('Passkey authentication cancelled');

  const response = credential.response as AuthenticatorAssertionResponse;

  // 4. Send credential to server
  const authRes = await fetchWithCsrf('/passkey_session', {
    method: 'POST',
    body: JSON.stringify({
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(response.authenticatorData),
          clientDataJSON: bufferToBase64url(response.clientDataJSON),
          signature: bufferToBase64url(response.signature),
          userHandle: response.userHandle
            ? bufferToBase64url(response.userHandle)
            : null,
        },
      },
    }),
  });

  if (authRes.status === 423) {
    const err = await authRes.json();
    throw new PasskeyRejectedError(err.error);
  }

  if (!authRes.ok) {
    throw new Error('Authentication failed');
  }

  const result = await authRes.json();
  return result.redirect;
}
